import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import { batch } from 'react-redux'
import { useDebounce, useWindowSize } from 'usehooks-ts'

import EmptyState from '@/components/EmptyState'
import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import Pagination from '@/modules/media/components/Pagination'
import NewsCard from '@/modules/media/press/components/NewsCard'
import SearchState from '@/modules/media/press/components/SearchState'
import { appSetIsLoading } from '@/reducers/app'
import {
  pressReleaseSetIsEventFilter,
  pressReleaseSetSelected,
  pressReleaseSetSkip,
  pressReleaseSetTags,
  pressReleaseSetTitle,
} from '@/reducers/press-release'
import { useLazyGetNewsQuery } from '@/services/news'
import { formatEpoch } from '@/utils/formatter'
import { useAppDispatch, useAppSelector } from '@/utils/hooks'

import EmptyStateImage from '~/assets/images/media/press-empty-state.webp'
import NotFoundStateImage from '~/assets/images/media/press-not-found-state.webp'

const PressReleaseContent = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const pressReleaseIsEventFilter = useAppSelector((state) => state.pressRelease.isEventFilter)
  const pressReleaseTitle = useAppSelector((state) => state.pressRelease.title)
  const pressReleaseSelected = useAppSelector((state) => state.pressRelease.selected)
  const pressReleaseSkip = useAppSelector((state) => state.pressRelease.skip)
  const pressReleaseLimit = useAppSelector((state) => state.pressRelease.limit)
  const pressReleaseTags = useAppSelector((state) => state.pressRelease.tags)
  const { width } = useWindowSize()
  const debouncedPressReleaseTitle = useDebounce<string>(pressReleaseTitle, width <= 768 ? 1000 : 500)
  const [getNews, responseNews] = useLazyGetNewsQuery()
  const [getNewsHighlight, responseNewsHighlight] = useLazyGetNewsQuery()

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * pressReleaseLimit) % Number(responseNews?.data?.data.metadata.total)
    batch(() => {
      dispatch(pressReleaseSetSelected(event.selected))
      dispatch(pressReleaseSetSkip(newOffset))
      dispatch(pressReleaseSetIsEventFilter(true))
    })
  }

  const fetchingNewHighlight = () => {
    getNewsHighlight({
      params: { skip: 0, limit: 3, keyword: { isHighlight: 1 } },
      headers: { 'Accept-Language': String(router.locale) },
    })
  }
  const fetchingNew = () => {
    const keyword: any = { isHighlight: 0 }
    if (pressReleaseTitle.length > 0) {
      keyword.title = pressReleaseTitle
    }
    if (pressReleaseTags.length > 0) {
      keyword.tags = pressReleaseTags
    }
    getNews({
      params: {
        skip: pressReleaseSkip,
        limit: pressReleaseLimit,
        keyword,
      },
      headers: { 'Accept-Language': String(router.locale) },
    })
  }

  const handleReset = () => {
    batch(() => {
      dispatch(pressReleaseSetIsEventFilter(true))
      dispatch(pressReleaseSetSkip(0))
      dispatch(pressReleaseSetSelected(0))
      dispatch(pressReleaseSetTitle(''))
    })
  }

  useEffect(() => {
    fetchingNewHighlight()
    fetchingNew()
  }, [])

  useEffect(() => {
    if (pressReleaseIsEventFilter) {
      dispatch(pressReleaseSetIsEventFilter(false))
    }
    dispatch(appSetIsLoading(responseNews.isFetching))
  }, [responseNews])

  useEffect(() => {
    if (pressReleaseIsEventFilter) {
      fetchingNew()
    }
  }, [pressReleaseIsEventFilter, debouncedPressReleaseTitle, pressReleaseTitle, pressReleaseSelected, pressReleaseTags])

  useEffect(() => {
    if (router?.query.tag) {
      dispatch(pressReleaseSetTags([String(router?.query.tag)]))
    }
  }, [router?.query.tag])

  return (
    <section className='container flex w-full flex-col pb-8 pt-4 lg:py-8 lg:pb-12.5'>
      <>
        {!(pressReleaseTitle.length > 0) &&
        !(pressReleaseTags.length > 0) &&
        responseNewsHighlight?.data?.data.items &&
        responseNewsHighlight?.data?.data.items.length > 0 ? (
          <>
            <h1 className='text-2xl font-bold'>{t('press:title')}</h1>

            <div className='flex flex-col gap-6 border-b-[1px] border-b-neutral-border py-6 lg:flex-row lg:gap-[30px]'>
              <article className='group relative h-[193px] w-full overflow-hidden rounded-lg border-[1px] border-neutral-border transition-all duration-300 hover:shadow-card lg:h-[411px] lg:w-[730px]'>
                <div className='h-[118px] w-full overflow-hidden lg:h-[335px]'>
                  <ImageByWidthDevice
                    images={responseNewsHighlight?.data?.data.items[0].imageFiles}
                    alt={responseNewsHighlight?.data?.data.items[0].imageFiles.desktop.fileName ?? ''}
                    className='h-[118px] w-full rounded-t-lg object-cover object-top transition-all duration-300 group-hover:scale-125 lg:h-[335px] lg:group-hover:brightness-50'
                  />
                </div>

                <div className='absolute z-10 flex h-fit w-full flex-col gap-3.5 bg-white p-4 pt-3 transition-all duration-300 md:py-3.5 lg:-bottom-28 lg:group-hover:bottom-0'>
                  <Link href={`/media/press/${responseNewsHighlight?.data?.data.items[0].slug}`}>
                    <h4 className='line-clamp-2 h-12 cursor-pointer text-[14px] font-semibold leading-[21px] text-dark'>
                      {responseNewsHighlight?.data?.data.items[0].title ?? ''}
                    </h4>
                  </Link>

                  <p className='line-clamp-2 hidden h-9 break-all text-xs leading-normal text-gray-2 md:block'>
                    {responseNewsHighlight?.data?.data.items[0].shortDescription ?? ''}
                  </p>

                  <div className='flex items-center justify-between border-t border-t-accent-light/10 pt-3'>
                    <p className='text-xs leading-normal text-gray-2'>
                      {formatEpoch(
                        Number(responseNewsHighlight?.data?.data.items[0].createdAt),
                        'EEEE, dd LLLL yyyy | T',
                        router.locale
                      )}
                    </p>

                    <Link href={`/media/press/${responseNewsHighlight?.data?.data.items[0].slug}`}>
                      <span className='kek-arrow-right cursor-pointer text-[24px]' />
                    </Link>
                  </div>
                </div>
              </article>

              <div className='flex flex-col gap-6 lg:gap-[17px]'>
                {responseNewsHighlight?.data?.data.items.slice(1, 3).map((news) => (
                  <article
                    className='group relative h-[193px] w-full overflow-hidden rounded-lg border-[1px] border-neutral-border lg:h-[197px] lg:w-[350px]'
                    key={news.xid}
                  >
                    <div className='h-[118px] w-full overflow-hidden lg:h-[137px]'>
                      <ImageByWidthDevice
                        images={news.imageFiles}
                        alt={news.imageFiles.desktop.fileName ?? ''}
                        className='h-[118px] w-full rounded-t-lg object-cover object-top transition-all duration-300 group-hover:scale-125 lg:h-[137px]'
                      />
                    </div>

                    <div className='px-4 py-3 lg:px-3'>
                      <Link href={`/media/press/${news.slug}`}>
                        <h4 className='line-clamp-2 cursor-pointer text-[14px] font-semibold leading-[21px] tracking-[-0.084px] text-dark lg:text-[12px] lg:leading-[18px] lg:tracking-normal'>
                          {news.title}
                        </h4>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </>
        ) : (
          <SearchState total={responseNews?.data?.data.metadata.total ?? 0} />
        )}

        {!responseNews.isFetching && responseNews?.data?.data && (
          <>
            <div className='mb-6 mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:mb-12 lg:grid-cols-3 lg:gap-7.5'>
              {responseNews?.data &&
                responseNews?.data?.data.items.map((news) => (
                  <NewsCard
                    data={news}
                    key={news.xid}
                  />
                ))}
            </div>

            <Pagination
              onPageChange={handlePageClick}
              selected={pressReleaseSelected}
              perPage={pressReleaseLimit}
              totalData={responseNews?.data?.data.metadata.total ?? 0}
            />
          </>
        )}

        {pressReleaseTitle.length > 0 && !responseNews.isFetching && responseNews?.data?.data.items.length == 0 && (
          <EmptyState
            img={NotFoundStateImage.src}
            title={t('press:notFoundState.title')}
            description={t('press:notFoundState.description')}
            imgClass='mx-auto w-[166px] h-[161px] lg:w-[333px] lg:h-[321px]'
            containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px]'
            buttonText={t('press:notFoundState.button') || undefined}
            onClick={handleReset}
          />
        )}

        {pressReleaseTitle.length === 0 && !responseNews.isFetching && responseNews?.data?.data.items.length === 0 && (
          <EmptyState
            img={EmptyStateImage.src}
            title={t('press:emptyState.title')}
            description={t('press:emptyState.description')}
            imgClass='mx-auto w-[166px] h-[161px] lg:w-[333px] lg:h-[321px]'
            containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px]'
          />
        )}
      </>
    </section>
  )
}

export default PressReleaseContent
